import { useState, useRef, useEffect, useCallback } from "react";
import VoiceDemo from "./VoiceDemo";
import "../styles/voiceDemo.css";
import { useLang } from "../lib/LangContext";

/* Calendly endpoints — language-routed */
const CALENDLY_URLS = {
  en: "https://calendly.com/harivaghasiya301/kreux-matrix-architecture-consultation",
  de: "https://calendly.com/harivaghasiya301/kreux-matrix-architecture-beratung",
};

/* =================================================================
 * Kreux Matrix — Page 2 — Operations & Core Blueprint Layer
 * Gated user journey:
 *   Hero CTA  →  Section 1 (3 cores row) + click → right-slide modal
 *   CORE II modal → [Run Core II Logic Simulator] → Section 3 (video)
 *   Section 3 video → 10s → Amber alert YES/NO
 *     NO  → resume video
 *     YES → unlock Section 2 + Section 4, scroll down
 * Sections 2, 3, 4 are display:none on initial load.
 * ================================================================= */

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ff176637-490c-4d8d-8821-0039665e7b20/artifacts/1ppcjvci_Web%20logo.png";

/* ───────────────── Bespoke inline SVG icons ───────────────── */

const ChevronRightIcon = (p) => (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" {...p}>
    <path d="M5 3 L11 8 L5 13" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" />
  </svg>
);
const CloseIcon = (p) => (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" {...p}>
    <path d="M4 4 L12 12 M12 4 L4 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" />
  </svg>
);
const PlayIcon = (p) => (
  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" {...p}>
    <polygon points="5,3 17,10 5,17" fill="currentColor" />
  </svg>
);
const CheckIcon = ({ size = 14 }) => (
  <svg viewBox="0 0 16 16" width={size} height={size} aria-hidden="true">
    <path d="M3 8.5 L7 12 L13 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="square" />
  </svg>
);
const DocMessyIcon = ({ size = 56 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
    <path d="M14 8 H42 L52 18 V56 H14 Z" fill="rgba(27,50,69,0.65)" stroke="rgba(208,218,232,0.4)" strokeWidth="1" />
    <path d="M42 8 V18 H52" fill="none" stroke="rgba(208,218,232,0.4)" strokeWidth="1" />
    <path d="M20 26 H44 M20 32 H40 M20 38 H42 M20 44 H36" stroke="rgba(208,218,232,0.32)" strokeWidth="0.8" />
  </svg>
);
const EnvelopeIcon = ({ size = 36 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
    <rect x="8" y="16" width="48" height="32" fill="rgba(27,50,69,0.75)" stroke="currentColor" strokeWidth="1.1" />
    <path d="M8 16 L32 36 L56 16" fill="none" stroke="currentColor" strokeWidth="1.1" />
  </svg>
);
const DatabaseIcon = ({ size = 36 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
    <ellipse cx="32" cy="14" rx="20" ry="6" fill="rgba(27,50,69,0.75)" stroke="currentColor" strokeWidth="1.1" />
    <path d="M12 14 V30 C12 33 21 36 32 36 C43 36 52 33 52 30 V14" fill="rgba(17,35,50,0.7)" stroke="currentColor" strokeWidth="1.1" />
    <path d="M12 30 V46 C12 49 21 52 32 52 C43 52 52 49 52 46 V30" fill="rgba(17,35,50,0.7)" stroke="currentColor" strokeWidth="1.1" />
  </svg>
);
const InvoiceIcon = ({ size = 36 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
    <path d="M14 6 H44 L52 14 V58 H14 Z" fill="rgba(27,50,69,0.75)" stroke="currentColor" strokeWidth="1.1" />
    <path d="M44 6 V14 H52" fill="none" stroke="currentColor" strokeWidth="1.1" />
    <path d="M22 24 H44 M22 32 H44 M22 40 H38" stroke="currentColor" strokeWidth="0.9" />
  </svg>
);
const CrmIcon = ({ size = 28 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
    <circle cx="22" cy="22" r="8" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="42" cy="22" r="8" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <path d="M10 50 C10 40 18 34 32 34 C46 34 54 40 54 50" fill="none" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);
const ErpIcon = ({ size = 28 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
    <rect x="10" y="10" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <rect x="34" y="10" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <rect x="10" y="34" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <rect x="34" y="34" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);
const FinanceIcon = ({ size = 28 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
    <path d="M8 50 L20 36 L32 42 L46 22 L56 28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
    <circle cx="56" cy="28" r="2.5" fill="currentColor" />
  </svg>
);
const LegalIcon = ({ size = 28 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
    <path d="M32 8 V14 M16 14 H48 M20 14 L14 30 H26 L20 14 Z M44 14 L38 30 H50 L44 14 Z M28 50 H36 V40 H32 V14 M22 54 H42" fill="none" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

const CORES = [
  {
    id: "voice",
    alpha: "CORE I",
    name: "VOICE",
    sub: "Automated Phone & Voice Operations",
    surface: "Conversational Elegance. Zero Latency.",
    deep:
      "Re-engineer how your business speaks. Voice deploys stunningly lifelike verbal engines that manage complex customer dialogue and outbound phone operations. Free from robotic delays, it delivers an elite brand experience 24 hours a day.",
    benefit:
      "Perfect for organizations facing massive call volumes who refuse to compromise on elite client care.",
  },
  {
    id: "logic",
    alpha: "CORE II",
    name: "LOGIC",
    sub: "Digital Data & Workflow Sync",
    surface: "Invisible Orchestration. Flawless Execution.",
    deep:
      "Eradicate the friction of manual digital labor. Logic operates silently in the background of your enterprise—reading complex data, syncing disparate systems, and executing multi-step operations instantly.",
    benefit:
      "Designed for businesses drowning in administrative data entry, invoices, and cross-platform management.",
    hasSimulator: true,
  },
  {
    id: "sovereign",
    alpha: "CORE III",
    name: "SOVEREIGN",
    sub: "Bespoke Private Systems",
    surface: "Proprietary Architecture. Absolute Independence.",
    deep:
      "Reserved for enterprises requiring a completely unique competitive edge. Sovereign connects your leadership directly with our elite engineering team to design, train, and deploy private models built entirely from the ground up.",
    benefit:
      "Reserved for corporations requiring bespoke, highly secure, and entirely proprietary assets. By private consultation only.",
  },
];

const STREAM_DESTINATIONS = [
  { id: "crm", name: "CRM", Icon: CrmIcon, message: "[Updated] Account upgraded to Enterprise." },
  { id: "erp", name: "ERP", Icon: ErpIcon, message: "[Updated] 500 licenses provisioned automatically." },
  { id: "finance", name: "FINANCE", Icon: FinanceIcon, message: "[Generated] Invoice #9910 created and routed." },
  { id: "legal", name: "LEGAL", Icon: LegalIcon, message: "[Archived] Securely stored in encrypted matrix repository." },
];

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

const OperationsLayer = () => {
  /* Site-wide language (EN/DE) from global context — drives all UI strings,
   * Voice Demo assets, Calendly routing. */
  const { lang, setLang, t } = useLang();

  /* Gated journey state */
  const [showCores, setShowCores] = useState(false);
  const [coresFanOpen, setCoresFanOpen] = useState(false);
  const [openCore, setOpenCore] = useState(null); // index 0..2

  const [showVideo, setShowVideo] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0); // 0..1
  const [showAlert, setShowAlert] = useState(false);
  const videoTickRef = useRef(null);
  const videoTimerRef = useRef(null);

  const [showStream, setShowStream] = useState(false);
  const [showTactile, setShowTactile] = useState(false);

  /* Stream sim state */
  const [streamPhase, setStreamPhase] = useState(0);
  const [streamLogs, setStreamLogs] = useState([]);
  const [destStates, setDestStates] = useState([false, false, false, false]);
  const [scorecard, setScorecard] = useState(false);
  const streamLockRef = useRef(false);
  const logListRef = useRef(null);

  /* Tactile state */
  const [simRunning, setSimRunning] = useState(false);
  const [simNodeIdx, setSimNodeIdx] = useState(-1);
  const [simDone, setSimDone] = useState(false);
  const simLockRef = useRef(false);

  /* Consultation form state */
  const [showConsult, setShowConsult] = useState(false);
  const [consult, setConsult] = useState({
    name: "", company: "", email: "", phone: "", brief: "", country: "US",
  });
  const [consultErr, setConsultErr] = useState({});
  const [consultDone, setConsultDone] = useState(false);
  const [showCountryDD, setShowCountryDD] = useState(false);
  const COUNTRIES = [
    { c: "US", flag: "🇺🇸", code: "+1", name: "United States" },
    { c: "GB", flag: "🇬🇧", code: "+44", name: "United Kingdom" },
    { c: "AE", flag: "🇦🇪", code: "+971", name: "UAE" },
    { c: "SG", flag: "🇸🇬", code: "+65", name: "Singapore" },
    { c: "DE", flag: "🇩🇪", code: "+49", name: "Germany" },
    { c: "FR", flag: "🇫🇷", code: "+33", name: "France" },
    { c: "CH", flag: "🇨🇭", code: "+41", name: "Switzerland" },
    { c: "AU", flag: "🇦🇺", code: "+61", name: "Australia" },
    { c: "CA", flag: "🇨🇦", code: "+1", name: "Canada" },
    { c: "IN", flag: "🇮🇳", code: "+91", name: "India" },
    { c: "JP", flag: "🇯🇵", code: "+81", name: "Japan" },
    { c: "SA", flag: "🇸🇦", code: "+966", name: "Saudi Arabia" },
  ];
  const PUBLIC_DOMAINS = new Set([
    "gmail.com","yahoo.com","hotmail.com","outlook.com","icloud.com","aol.com",
    "proton.me","protonmail.com","mail.com","gmx.com","live.com","msn.com",
    "yandex.com","zoho.com","rediffmail.com",
  ]);
  const selectedCountry = COUNTRIES.find((x) => x.c === consult.country) || COUNTRIES[0];

  const validateConsult = () => {
    const err = {};
    if (!consult.name.trim()) err.name = t("consult.err.required");
    if (!consult.company.trim()) err.company = t("consult.err.required");
    if (!consult.brief.trim() || consult.brief.trim().length < 12)
      err.brief = t("consult.err.brief");
    const email = consult.email.trim().toLowerCase();
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!re.test(email)) err.email = t("consult.err.emailInvalid");
    else {
      const domain = email.split("@")[1];
      if (PUBLIC_DOMAINS.has(domain))
        err.email = t("consult.err.emailPublic");
    }
    if (!consult.phone.trim() || !/^\d[\d\s-]{4,}$/.test(consult.phone.trim()))
      err.phone = t("consult.err.phoneInvalid");
    setConsultErr(err);
    return Object.keys(err).length === 0;
  };
  const submitConsult = (e) => {
    e.preventDefault();
    if (!validateConsult()) return;
    setConsultDone(true);
    // Build prefill params and route to language-matched Calendly endpoint
    const base = CALENDLY_URLS[lang] || CALENDLY_URLS.en;
    const params = new URLSearchParams({
      name: consult.name.trim(),
      email: consult.email.trim(),
      a1: consult.company.trim(),
      a2: `${selectedCountry.code} ${consult.phone.trim()}`,
      a3: consult.brief.trim(),
    });
    const target = `${base}?${params.toString()}`;
    setTimeout(() => {
      // Open Calendly in a new tab so the luxury page state remains intact
      window.open(target, "_blank", "noopener,noreferrer");
    }, 1600);
    setTimeout(() => {
      setShowConsult(false);
      setConsultDone(false);
      setConsult({ name: "", company: "", email: "", phone: "", brief: "", country: "US" });
    }, 3200);
  };
  const openConsult = () => { setConsultErr({}); setConsultDone(false); setShowConsult(true); };

  /* CORE I — chat UI seed (placeholder for ElevenLabs voice later) */
  const [chatLog] = useState([
    { who: "agent", text: "Good afternoon. This is Aria from Kreux Matrix. How may I direct your call today?" },
    { who: "user",  text: "I’d like to schedule a private consultation regarding our enterprise stack." },
    { who: "agent", text: "Absolutely. I’ve pulled your prior dossier. Would Thursday at 3:15 PM CET suit your calendar?" },
    { who: "user",  text: "Yes — please send the calendar invite to my corporate email." },
    { who: "agent", text: "Done. Invite dispatched. Anything else I can prepare for the call?" },
  ]);

  /* Scroll helpers */
  const scrollTo = (id, block = "start") => {
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block });
    }, 80);
  };

  /* Hero CTA no longer deep-links to CORE II — it now opens the Architecture
   * lead-capture modal owned by Hero. Routing intentionally removed per spec. */

  /* Lock body scroll while modal is open */
  useEffect(() => {
    if (openCore !== null) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [openCore]);

  /* Global bridge — allows the top-nav to open any Core modal instantly,
     bypassing scroll. On-page buttons continue to call setOpenCore directly. */
  useEffect(() => {
    const handler = (e) => {
      const idx = typeof e.detail === "number" ? e.detail : e.detail?.index;
      if (idx === 0 || idx === 1 || idx === 2) setOpenCore(idx);
    };
    window.addEventListener("kreux:open-core", handler);
    return () => window.removeEventListener("kreux:open-core", handler);
  }, []);

  /* Run Core II Logic Simulator — unlocks Section 3 only */
  const launchVideo = () => {
    setOpenCore(null);
    setShowVideo(true);
    setShowAlert(false);
    setVideoProgress(0);
    setTimeout(() => {
      scrollTo("matrix-section-video", "start");
      // begin playback after brief delay so scroll completes
      setTimeout(() => setVideoPlaying(true), 700);
    }, 80);
  };

  /* Global bridge — top-nav "Experience the Digital Assistant" bypasses the
     intermediate Logic dossier and launches the Core II simulator directly. */
  useEffect(() => {
    const handler = () => launchVideo();
    window.addEventListener("kreux:launch-logic-demo", handler);
    return () => window.removeEventListener("kreux:launch-logic-demo", handler);
  }, []);

  /* Video playback timer — 10s → amber alert */
  useEffect(() => {
    if (!videoPlaying) {
      if (videoTickRef.current) clearInterval(videoTickRef.current);
      if (videoTimerRef.current) clearTimeout(videoTimerRef.current);
      return;
    }
    // 10s total video, tick every 100ms
    const start = Date.now() - videoProgress * 10000;
    videoTickRef.current = setInterval(() => {
      const elapsed = (Date.now() - start) / 10000;
      const clamped = Math.min(elapsed, 1);
      setVideoProgress(clamped);
    }, 100);
    videoTimerRef.current = setTimeout(() => {
      setVideoPlaying(false);
      setShowAlert(true);
    }, (1 - videoProgress) * 10000);
    return () => {
      if (videoTickRef.current) clearInterval(videoTickRef.current);
      if (videoTimerRef.current) clearTimeout(videoTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPlaying]);

  const alertNo = () => {
    setShowAlert(false);
    setVideoPlaying(true);
  };
  const alertYes = () => {
    setShowAlert(false);
    setShowStream(true);
    setShowTactile(true);
    scrollTo("matrix-section-stream", "start");
  };

  /* Stream simulation */
  const pushLog = useCallback((line) => {
    setStreamLogs((p) => [...p, { id: p.length + "_" + Math.random(), text: line }]);
  }, []);
  useEffect(() => {
    if (logListRef.current) logListRef.current.scrollTop = logListRef.current.scrollHeight;
  }, [streamLogs]);
  const runStream = useCallback(async () => {
    if (streamLockRef.current) return;
    streamLockRef.current = true;
    setStreamLogs([]);
    setDestStates([false, false, false, false]);
    setScorecard(false);

    setStreamPhase(1);
    await delay(220);
    pushLog(t("section2.logs.ingest"));
    await delay(620);
    pushLog(t("section2.logs.scan"));
    await delay(620);

    setStreamPhase(2);
    await delay(220);
    pushLog(t("section2.logs.extract"));
    await delay(560);
    pushLog(t("section2.logs.compliance"));
    await delay(560);
    pushLog(t("section2.logs.formatting"));
    await delay(560);

    setStreamPhase(3);
    await delay(160);
    for (let i = 0; i < 4; i++) {
      setDestStates((p) => {
        const n = [...p];
        n[i] = true;
        return n;
      });
      await delay(420);
    }
    await delay(400);

    setStreamPhase(4);
    setScorecard(true);
    await delay(5200);
    setStreamPhase(0);
    setScorecard(false);
    setDestStates([false, false, false, false]);
    setStreamLogs([]);
    streamLockRef.current = false;
  }, [pushLog, t]);

  /* Tactile simulator */
  const runSimulator = useCallback(async () => {
    if (simLockRef.current) return;
    simLockRef.current = true;
    setSimDone(false);
    setSimRunning(true);
    for (let i = 0; i < 3; i++) {
      setSimNodeIdx(i);
      await delay(520);
    }
    setSimNodeIdx(-1);
    setSimRunning(false);
    setSimDone(true);
    await delay(6000);
    setSimDone(false);
    simLockRef.current = false;
  }, []);

  return (
    <section
      id="matrix-operations-layer"
      data-testid="operations-layer"
      aria-label="Kreux Matrix — Operations & Core Blueprint"
    >
      <div className="mol-bg" aria-hidden="true">
        <div className="mol-bg__gridA" />
        <div className="mol-bg__gridB" />
      </div>

      <div className="mol-wrap">
        {/* ───────────── SECTION 1 — Cores Selection Menu ───────────── */}
        <section
          id="matrix-section-cores"
          className={"mol-section mol-section--cores" + (showCores ? " mol-section--open" : "")}
          aria-hidden={!showCores}
          data-testid="mol-section-cores"
        >
          <header className="mol-head">
            <span className="mol-eyebrow">
              <span className="mol-eyebrow__rule" />
              <span>{t("section1.eyebrow")}</span>
              <span className="mol-eyebrow__rule" />
            </span>
            <h2 className="mol-title">
              {t("section1.titlePre")} <em className="mol-title__em">{t("section1.titleEm")}</em> {t("section1.titleSuffix")}
            </h2>
            <p className="mol-body">
              {t("section1.body")}
            </p>
          </header>

          <div className="mol-cores" role="list">
            {CORES.map((c, i) => (
              <button
                key={c.id}
                type="button"
                className="mol-core"
                data-testid={`mol-core-card-${c.id}`}
                onClick={() => setOpenCore(i)}
                role="listitem"
              >
                <span className="mol-core__corners" aria-hidden="true">
                  <span /><span /><span /><span />
                </span>
                <div className="mol-core__top">
                  <span className="mol-core__alpha">{c.alpha}</span>
                  <span className="mol-core__dot" />
                  <span className="mol-core__plain">{t("section1.dossier")}</span>
                </div>
                <h3 className="mol-core__name">{c.name}</h3>
                <p className="mol-core__sub">{t(`cores.${c.id}.sub`)}</p>
                <p className="mol-core__surface">{t(`cores.${c.id}.surface`)}</p>
                <span className="mol-core__cta">
                  {t("section1.openDossier")} <ChevronRightIcon />
                </span>
              </button>
            ))}
          </div>

          <div className="mol-footer-cta" data-testid="mol-section1-footer-ctas">
            <button
              type="button"
              className={"mol-cta mol-cta--ghost" + (coresFanOpen ? " is-fanned" : "")}
              onClick={() => {
                setShowCores(true);
                setCoresFanOpen((v) => !v);
                scrollTo("matrix-section-cores", "start");
              }}
              aria-expanded={coresFanOpen}
              aria-controls="mol-core-fan"
              data-testid="mol-cta-experience-cores"
            >
              <span>{t("section1.experienceCores")}</span>
              <ChevronRightIcon />
            </button>
            <a
              href="#consultation"
              className="mol-cta mol-cta--primary"
              data-testid="mol-cta-private-consult"
              onClick={(e) => { e.preventDefault(); openConsult(); }}
            >
              <span>{t("section1.privateConsult")}</span>
              <ChevronRightIcon />
              <span className="mol-cta__glow" aria-hidden="true" />
            </a>
          </div>

          {/* Inline reveal — staggered sub-options inheriting ghost CTA styling */}
          <div
            id="mol-core-fan"
            className={"mol-core-fan" + (coresFanOpen ? " is-open" : "")}
            role="group"
            aria-hidden={!coresFanOpen}
            data-testid="mol-core-fan"
          >
            <button
              type="button"
              className="mol-cta mol-cta--ghost mol-cta--sm mol-core-fan__btn"
              onClick={() => {
                setOpenCore(0);
                scrollTo("matrix-section-cores", "start");
              }}
              tabIndex={coresFanOpen ? 0 : -1}
              data-testid="mol-experience-core-1"
            >
              <span className="mol-core-fan__alpha">CORE I</span>
              <span>{t("section1.experienceCore1")}</span>
              <ChevronRightIcon />
            </button>
            <button
              type="button"
              className="mol-cta mol-cta--ghost mol-cta--sm mol-core-fan__btn"
              onClick={() => {
                setOpenCore(1);
                scrollTo("matrix-section-cores", "start");
              }}
              tabIndex={coresFanOpen ? 0 : -1}
              data-testid="mol-experience-core-2"
            >
              <span className="mol-core-fan__alpha">CORE II</span>
              <span>{t("section1.experienceCore2")}</span>
              <ChevronRightIcon />
            </button>
          </div>
        </section>
      </div>

      {/* ───────────── Centered Glassmorphic Core Dossier Modal ───────────── */}
      <div
        className={"mol-modal-root fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0A1520]/80 backdrop-blur-xl" + (openCore !== null ? " is-open" : "")}
        aria-hidden={openCore === null}
        data-testid="mol-modal-root"
      >
        <div className="mol-modal__scrim" onClick={() => setOpenCore(null)} />
        <aside className="mol-modal relative w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-2xl shadow-[0_0_50px_rgba(0,255,255,0.15)] p-8 md:p-12" role="dialog" aria-modal="true">
          {openCore !== null && (() => {
            const c = CORES[openCore];
            return (
              <>
                <header className="mol-modal__head">
                  <div className="mol-modal__alphaRow">
                    <span className="mol-modal__alpha">{c.alpha}</span>
                    <span className="mol-modal__alphaDot" />
                    <span className="mol-modal__plain">{t(`cores.${c.id}.sub`)}</span>
                  </div>
                  <div className="mol-consult__headActions">
                    {c.id === "voice" && (
                      <div className="voice-lang" role="group" aria-label="Voice demo language">
                        <button
                          type="button"
                          className={"voice-lang__btn" + (lang === "en" ? " is-on" : "")}
                          onClick={() => setLang("en")}
                          data-testid="voice-lang-en"
                        >EN</button>
                        <button
                          type="button"
                          className={"voice-lang__btn" + (lang === "de" ? " is-on" : "")}
                          onClick={() => setLang("de")}
                          data-testid="voice-lang-de"
                        >DE</button>
                      </div>
                    )}
                    <button
                      type="button"
                      className="mol-modal__close"
                      onClick={() => setOpenCore(null)}
                      aria-label="Close core dossier"
                      data-testid="mol-modal-close"
                    >
                      <CloseIcon /> {t("modal.close")}
                    </button>
                  </div>
                </header>

                <div className="mol-modal__body">
                  <h3 className="mol-modal__name">{c.name}</h3>
                  <p className="mol-modal__surface">{c.surface}</p>

                  <div className="mol-modal__section">
                    <span className="mol-modal__label">{t("modal.deepLabel")}</span>
                    <p className="mol-modal__text">{t(`cores.${c.id}.deep`)}</p>
                  </div>

                  <div className="mol-modal__section mol-modal__section--benefit">
                    <span className="mol-modal__label mol-modal__label--amber">
                      <span className="mol-tick" /> {t("modal.benefitLabel")}
                    </span>
                    <p className="mol-modal__text">{t(`cores.${c.id}.benefit`)}</p>
                  </div>

                  {/* CORE I — Premium Voice Demo (simulated playback + glassmorphism overlay) */}
                  {c.id === "voice" && (
                    <VoiceDemo lang={lang} />
                  )}
                </div>

                <footer className="mol-modal__footer">
                  <button
                    type="button"
                    className="mol-cta mol-cta--ghost mol-cta--sm"
                    onClick={() => setOpenCore(null)}
                  >
                    <span>{t("section1.experienceCores")}</span>
                  </button>
                  {c.hasSimulator && (
                    <button
                      type="button"
                      className="mol-cta mol-cta--sim"
                      onClick={launchVideo}
                      data-testid="mol-run-core-ii"
                    >
                      <span>{t("modal.runSim")}</span>
                      <PlayIcon />
                    </button>
                  )}
                  <button
                    type="button"
                    className="mol-cta mol-cta--primary mol-cta--sm"
                    onClick={() => { setOpenCore(null); setTimeout(openConsult, 320); }}
                    data-testid="mol-modal-private-consult"
                  >
                    <span>{t("section1.privateConsult")}</span>
                    <span className="mol-cta__glow" aria-hidden="true" />
                  </button>
                </footer>
              </>
            );
          })()}
        </aside>
      </div>

      <div className="mol-wrap">
        {/* ───────────── SECTION 3 — Side-by-Side Speedrun (gated) ───────────── */}
        {showVideo && (
          <section
            id="matrix-section-video"
            className="mol-section mol-section--video mol-section--open"
            data-testid="mol-section-video"
          >
            <header className="mol-head mol-head--inline">
              <span className="mol-eyebrow">
                <span className="mol-eyebrow__rule" />
                <span>{t("section3.eyebrow")}</span>
              </span>
              <h3 className="mol-subtitle">
                {t("section3.title1")} <span className="mol-arrow">{t("section3.titleVs")}</span> {t("section3.title2")}
              </h3>
            </header>

            <div className="mol-video">
              <span className="mol-video__corner mol-video__corner--tl" />
              <span className="mol-video__corner mol-video__corner--tr" />
              <span className="mol-video__corner mol-video__corner--bl" />
              <span className="mol-video__corner mol-video__corner--br" />
              <div className="mol-video__grid" aria-hidden="true" />

              <div className="mol-video__split">
                {/* LEFT — The Old Way */}
                <div className="mol-video__pane mol-video__pane--old">
                  <span className="mol-video__paneTag">{t("section3.oldWayTag")}</span>
                  <video
                    className="mol-video__src mol-video__src--old"
                    data-testid="mol-video-old"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                  >
                    <source src="https://ik.imagekit.io/5io9hzxfy/video_1920_1080_25fps.mp4" type="video/mp4" />
                  </video>
                  <div className={"mol-video__scene mol-video__scene--old" + (videoPlaying ? " is-playing" : "")}>
                    {/* Desktop wallpaper grain */}
                    <div className="mol-os" aria-hidden="true">
                      <span className="mol-os__bar" />
                      <span className="mol-os__bar mol-os__bar--b" />

                      {/* Email window with PDF attachment */}
                      <div className="mol-win mol-win--email">
                        <span className="mol-win__chrome">
                          <i /><i /><i />
                          <em>OUTLOOK — Inbox · Client Contract</em>
                        </span>
                        <div className="mol-win__body">
                          <span className="mol-mail" />
                          <span className="mol-mail mol-mail--b" />
                          <span className="mol-attach">
                            <span className="mol-attach__doc" />
                            <span className="mol-attach__name">Client_Contract_772.pdf</span>
                          </span>
                        </div>
                      </div>

                      {/* CRM window — rows being typed line-by-line */}
                      <div className="mol-win mol-win--crm">
                        <span className="mol-win__chrome">
                          <i /><i /><i />
                          <em>CRM — New Account Entry</em>
                        </span>
                        <div className="mol-win__body">
                          <span className="mol-row" />
                          <span className="mol-row mol-row--b" />
                          <span className="mol-row mol-row--c" />
                          <span className="mol-row mol-row--typing">
                            <span className="mol-caret" />
                          </span>
                        </div>
                      </div>

                      {/* QuickBooks dialog */}
                      <div className="mol-win mol-win--qb">
                        <span className="mol-win__chrome">
                          <i /><i /><i />
                          <em>QUICKBOOKS · Generating Receipt…</em>
                        </span>
                        <div className="mol-win__body mol-win__body--dialog">
                          <span className="mol-spinner" />
                          <span className="mol-progress">
                            <span className="mol-progress__fill" />
                          </span>
                          <span className="mol-progress__label">Processing… please wait</span>
                        </div>
                      </div>

                      {/* Operator silhouette */}
                      <div className="mol-operator">
                        <span className="mol-operator__head" />
                        <span className="mol-operator__body" />
                      </div>

                      {/* Cursor */}
                      <span className="mol-cursor" aria-hidden="true">
                        <svg viewBox="0 0 14 18" width="14" height="18">
                          <path d="M1 1 L13 8 L7 9 L10 16 L7 17 L4 10 L1 12 Z" fill="#E2EAF0" stroke="#112332" strokeWidth="0.6" />
                        </svg>
                      </span>
                    </div>
                    <span className="mol-video__timestamp">
                      00:{String(Math.floor(11 * (1 - videoProgress))).padStart(2, "0")}:
                      {String(Math.floor(60 * ((1 - videoProgress) % 1))).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="mol-video__overlay">
                    <span className="mol-video__indicator">
                      <PlayIcon /> <span>{t("section3.manualOperator")}</span>
                    </span>
                    <span className="mol-video__label">{t("section3.manualLabel")}</span>
                  </div>
                </div>

                <div className="mol-video__seam" aria-hidden="true" />

                {/* RIGHT — The Kreux Matrix Way */}
                <div className="mol-video__pane mol-video__pane--new">
                  <span className="mol-video__paneTag">{t("section3.newWayTag")}</span>
                  <video
                    className="mol-video__src mol-video__src--new"
                    data-testid="mol-video-new"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                  >
                    <source src="https://ik.imagekit.io/5io9hzxfy/section_3_video._202606121912.mp4?updatedAt=1781288146537" type="video/mp4" />
                  </video>
                  <div className={"mol-video__scene mol-video__scene--new" + (videoPlaying ? " is-playing" : "")}>
                    {/* Command-center dashboard */}
                    <div className="mol-cc" aria-hidden="true">
                      <span className="mol-cc__topbar">
                        <span><i className="mol-cc__dot" /> PIPELINE · ACTIVE</span>
                        <span>LATENCY · 38ms</span>
                        <span>THROUGHPUT · 1.4s</span>
                      </span>

                      <div className="mol-cc__stage">
                        {/* connector lines */}
                        <svg viewBox="0 0 320 200" className="mol-cc__lines" preserveAspectRatio="none">
                          <line x1="40" y1="60" x2="148" y2="100" stroke="rgba(0,229,255,0.7)" strokeWidth="1" />
                          <line x1="280" y1="60" x2="172" y2="100" stroke="rgba(0,229,255,0.7)" strokeWidth="1" />
                          <line x1="40" y1="140" x2="148" y2="100" stroke="rgba(0,229,255,0.7)" strokeWidth="1" />
                          <line x1="280" y1="140" x2="172" y2="100" stroke="rgba(0,229,255,0.7)" strokeWidth="1" />
                        </svg>
                        <svg viewBox="0 0 320 200" className="mol-cc__beams" preserveAspectRatio="none">
                          <line x1="40" y1="60" x2="148" y2="100" stroke="#00E5FF" strokeWidth="1.6" strokeDasharray="3 8" />
                          <line x1="280" y1="60" x2="172" y2="100" stroke="#00E5FF" strokeWidth="1.6" strokeDasharray="3 8" />
                          <line x1="40" y1="140" x2="148" y2="100" stroke="#00E5FF" strokeWidth="1.6" strokeDasharray="3 8" />
                          <line x1="280" y1="140" x2="172" y2="100" stroke="#00E5FF" strokeWidth="1.6" strokeDasharray="3 8" />
                        </svg>

                        <div className="mol-cc__tile mol-cc__tile--tl">
                          <span className="mol-cc__tileTag">EMAIL</span>
                          <span className="mol-cc__tileVal">INBOUND</span>
                          <span className="mol-cc__tileBar"><span /></span>
                        </div>
                        <div className="mol-cc__tile mol-cc__tile--tr">
                          <span className="mol-cc__tileTag">CRM</span>
                          <span className="mol-cc__tileVal">SYNCED</span>
                          <span className="mol-cc__tileBar"><span /></span>
                        </div>
                        <div className="mol-cc__tile mol-cc__tile--bl">
                          <span className="mol-cc__tileTag">ERP</span>
                          <span className="mol-cc__tileVal">SYNCED</span>
                          <span className="mol-cc__tileBar"><span /></span>
                        </div>
                        <div className="mol-cc__tile mol-cc__tile--br">
                          <span className="mol-cc__tileTag">FINANCE</span>
                          <span className="mol-cc__tileVal">RECEIPT · OK</span>
                          <span className="mol-cc__tileBar"><span /></span>
                        </div>

                        <div className="mol-cc__core">
                          <img src={LOGO_URL} alt="Kreux Matrix" className="mol-video__logo" />
                          <span className="mol-cc__corePulse" />
                        </div>
                      </div>

                      <span className="mol-cc__ticker">
                        <span>&gt; pipeline: contract.pdf → parsed</span>
                        <span>&gt; crm.upgrade(account, ENTERPRISE) ok</span>
                        <span>&gt; erp.provision(licenses, 500) ok</span>
                        <span>&gt; finance.invoice(#9910) ok</span>
                      </span>
                    </div>
                    <span className="mol-video__timestamp mol-video__timestamp--cyan">
                      00:00:0{(1.4 * (1 - videoProgress)).toFixed(1)}
                    </span>
                  </div>
                  <div className="mol-video__overlay">
                    <span className="mol-video__indicator mol-video__indicator--cyan">
                      <PlayIcon /> <span>{t("section3.autoEngine")}</span>
                    </span>
                    <span className="mol-video__label mol-video__label--cyan">
                      {t("section3.autoLabel")}
                    </span>
                  </div>
                </div>
              </div>

              {/* progress bar (subtle, at bottom) */}
              <div className="mol-video__progress" aria-hidden="true">
                <span style={{ width: `${videoProgress * 100}%` }} />
              </div>

              {/* Interruption overlay — amber alert */}
              {showAlert && (
                <div className="mol-alert" data-testid="mol-video-alert" role="alertdialog">
                  <div className="mol-alert__corners" aria-hidden="true">
                    <span /><span /><span /><span />
                  </div>
                  <span className="mol-alert__tag">{t("section3.alertTag")}</span>
                  <p className="mol-alert__prompt">
                    {t("section3.alertPrompt")}
                  </p>
                  <div className="mol-alert__actions">
                    <button
                      type="button"
                      className="mol-cta mol-cta--primary mol-cta--sm"
                      onClick={alertYes}
                      data-testid="mol-alert-yes"
                    >
                      <span>{t("section3.alertYes")}</span>
                      <span className="mol-cta__glow" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="mol-cta mol-cta--ghost mol-cta--sm"
                      onClick={alertNo}
                      data-testid="mol-alert-no"
                    >
                      <span>{t("section3.alertNo")}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ───────────── SECTION 2 — The Invisible Stream (gated) ───────────── */}
        {showStream && (
          <section
            id="matrix-section-stream"
            className={"mol-section mol-section--stream mol-section--open phase-" + streamPhase}
            data-testid="mol-section-stream"
          >
            <header className="mol-head mol-head--inline">
              <span className="mol-eyebrow">
                <span className="mol-eyebrow__rule" />
                <span>{t("section2.eyebrow")}</span>
              </span>
              <h3 className="mol-subtitle">
                {t("section2.titleSource")} <span className="mol-arrow">→</span> {t("section2.titleEngine")}{" "}
                <span className="mol-arrow">→</span> {t("section2.titleDestinations")}
              </h3>
            </header>

            <div className="mol-stream-frame">
              <div
                className={"mol-scorecard" + (scorecard ? " is-open" : "")}
                data-testid="mol-stream-scorecard"
                aria-hidden={!scorecard}
              >
                <div className="mol-scorecard__rule" />
                <div className="mol-scorecard__grid">
                  <div className="mol-scorecard__cell">
                    <span className="mol-scorecard__k">{t("section2.scoreSim")}</span>
                    <span className="mol-scorecard__v">{t("section2.scoreSimVal")}</span>
                  </div>
                  <div className="mol-scorecard__cell">
                    <span className="mol-scorecard__k">{t("section2.scoreTime")}</span>
                    <span className="mol-scorecard__v">{t("section2.scoreTimeVal")}</span>
                  </div>
                  <div className="mol-scorecard__cell">
                    <span className="mol-scorecard__k">{t("section2.scoreHuman")}</span>
                    <span className="mol-scorecard__v">{t("section2.scoreHumanVal")}</span>
                  </div>
                  <div className="mol-scorecard__cell">
                    <span className="mol-scorecard__k">{t("section2.scoreErr")}</span>
                    <span className="mol-scorecard__v">{t("section2.scoreErrVal")}</span>
                  </div>
                </div>
              </div>

              <div className="mol-stream-grid">
                <div className={"mol-col" + (streamPhase >= 1 ? " is-active" : "")}>
                  <span className="mol-col__tag">{t("section2.col1Tag")}</span>
                  <div className="mol-col__asset"><DocMessyIcon size={48} /></div>
                  <span className="mol-col__heading">{t("section2.col1Heading")}</span>
                  <span className="mol-col__sub">
                    {t("section2.col1IncomingPre")} <em>Client_Contract_772.pdf</em>
                  </span>
                </div>

                <div className={"mol-link" + (streamPhase >= 1 ? " is-lit" : "")} aria-hidden="true">
                  <span className="mol-link__line" />
                  <span className="mol-link__pulse" />
                </div>

                <div className={"mol-col mol-col--engine" + (streamPhase >= 2 ? " is-active" : "")}>
                  <span className="mol-col__tag">{t("section2.col2Tag")}</span>
                  <div className={"mol-col__asset mol-col__asset--logo" + (streamPhase >= 2 ? " is-glow" : "")}>
                    <img src={LOGO_URL} alt="Kreux Matrix" />
                  </div>
                  <span className="mol-col__heading">KREUX LOGIC™</span>
                  {streamPhase === 0 ? (
                    <span className="mol-col__sub">{t("section2.col2Idle")}</span>
                  ) : (
                    <span className="mol-col__sub is-live"><span className="mol-livedot" /> {t("section2.col2Live")}</span>
                  )}
                  <div className="mol-col__log" ref={logListRef} aria-live="polite">
                    {streamLogs.map((l) => (
                      <span key={l.id} className="mol-col__logline">{l.text}</span>
                    ))}
                  </div>
                </div>

                <div className={"mol-link" + (streamPhase >= 3 ? " is-lit" : "")} aria-hidden="true">
                  <span className="mol-link__line" />
                  <span className="mol-link__pulse" />
                </div>

                <div className={"mol-col mol-col--eco" + (streamPhase >= 3 ? " is-active" : "")}>
                  <span className="mol-col__tag">{t("section2.col3Tag")}</span>
                  <span className="mol-col__heading">{t("section2.col3Heading")}</span>
                  <span className="mol-col__sub">
                    {t("section2.statusPre")} {streamPhase < 3 ? t("section2.statusOutOfSync") : streamPhase === 3 ? t("section2.statusSyncing") : t("section2.statusOrchestrated")}
                  </span>
                  <ul className="mol-eco">
                    {STREAM_DESTINATIONS.map((d, i) => {
                      const done = destStates[i];
                      const Icon = d.Icon;
                      return (
                        <li key={d.id} className={"mol-eco__row" + (done ? " is-done" : "")}>
                          <span className="mol-eco__ico"><Icon size={20} /></span>
                          <span className="mol-eco__name">{d.name}</span>
                          <span className="mol-eco__msg">
                            {done ? (
                              <>
                                <span className="mol-eco__check"><CheckIcon size={10} /></span>
                                {t(`section2.dest.${d.id}`)}
                              </>
                            ) : (
                              <span className="mol-eco__pending">{t("section2.ecoPending")}</span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>

            <div className="mol-stream__actions">
              <button
                type="button"
                className={"mol-runsim" + (streamPhase > 0 ? " is-running" : "")}
                onClick={runStream}
                disabled={streamPhase > 0}
                data-testid="mol-run-simulation"
              >
                <span className="mol-runsim__dot" />
                <span>{streamPhase > 0 ? t("section2.runBtnRunning") : t("section2.runBtn")}</span>
                <ChevronRightIcon />
              </button>
            </div>
          </section>
        )}

        {/* ───────────── SECTION 4 — Tactile Simulator (gated) ───────────── */}
        {showTactile && (
          <section
            id="matrix-section-tactile"
            className="mol-section mol-section--tactile mol-section--open"
            data-testid="mol-section-tactile"
          >
            <header className="mol-head mol-head--inline">
              <span className="mol-eyebrow">
                <span className="mol-eyebrow__rule" />
                <span>{t("section4.eyebrow")}</span>
              </span>
              <h3 className="mol-subtitle">{t("section4.title")}</h3>
            </header>

            <div className="mol-tactile">
              <div className="mol-tactile__rail" aria-hidden="true">
                <span className={"mol-tactile__beam" + (simRunning ? " is-on" : "")} />
              </div>
              <div className="mol-tactile__nodes">
                {[
                  { id: "email", Icon: EnvelopeIcon },
                  { id: "db", Icon: DatabaseIcon },
                  { id: "invoice", Icon: InvoiceIcon },
                ].map((n, i) => {
                  const Icon = n.Icon;
                  const lit = simNodeIdx >= i && simRunning;
                  const passed = simNodeIdx > i || simDone;
                  return (
                    <div
                      key={n.id}
                      className={"mol-tnode" + (lit ? " is-lit" : "") + (passed ? " is-passed" : "")}
                      data-testid={`mol-tnode-${n.id}`}
                    >
                      <span className="mol-tnode__ring mol-tnode__ring--a" />
                      <span className="mol-tnode__ring mol-tnode__ring--b" />
                      <span className="mol-tnode__icon"><Icon size={32} /></span>
                      <span className="mol-tnode__label">{t(`section4.nodes.${n.id}.label`)}</span>
                      <span className="mol-tnode__caption">{t(`section4.nodes.${n.id}.caption`)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mol-tactile__actions">
              <button
                type="button"
                className={"mol-trigger" + (simRunning ? " is-running" : "")}
                onClick={runSimulator}
                disabled={simRunning}
                data-testid="mol-trigger-workflow"
              >
                <span className="mol-trigger__bracket">[</span>
                <span>{simRunning ? t("section4.triggerRunning") : t("section4.trigger")}</span>
                <span className="mol-trigger__bracket">]</span>
                <span className="mol-trigger__glow" aria-hidden="true" />
              </button>
            </div>

            <div
              className={"mol-tactile__success" + (simDone ? " is-on" : "")}
              data-testid="mol-tactile-success"
              aria-hidden={!simDone}
            >
              <span className="mol-tactile__check"><CheckIcon size={12} /></span>
              <span className="mol-tactile__msg">
                {t("section4.success")}
              </span>
            </div>
          </section>
        )}
      </div>

      {/* ───────────── Premium Consultation Form Modal ───────────── */}
      <div
        className={"mol-consult" + (showConsult ? " is-open" : "")}
        aria-hidden={!showConsult}
        data-testid="mol-consult-modal"
      >
        <div className="mol-consult__scrim" onClick={() => setShowConsult(false)} />
        <form className="mol-consult__panel" onSubmit={submitConsult} noValidate>
          <header className="mol-consult__head">
            <div>
              <span className="mol-consult__tag">PRIVATE CONSULTATION · {t("consult.tagSuffix")}</span>
              <h3 className="mol-consult__title">
                {t("consult.title")}
              </h3>
            </div>
            <div className="mol-consult__headActions">
              <div className="voice-lang" role="group" aria-label="Site language">
                <button
                  type="button"
                  className={"voice-lang__btn" + (lang === "en" ? " is-on" : "")}
                  onClick={() => setLang("en")}
                  data-testid="lang-en"
                >EN</button>
                <button
                  type="button"
                  className={"voice-lang__btn" + (lang === "de" ? " is-on" : "")}
                  onClick={() => setLang("de")}
                  data-testid="lang-de"
                >DE</button>
              </div>
              <button
                type="button"
                className="mol-modal__close"
                onClick={() => setShowConsult(false)}
                aria-label="Close consultation form"
              >
                <CloseIcon /> {t("consult.close")}
              </button>
            </div>
          </header>

          {consultDone ? (
            <div className="mol-consult__success">
              <span className="mol-tactile__check"><CheckIcon size={14} /></span>
              <p>
                {t("consult.success")}
              </p>
            </div>
          ) : (
            <div className="mol-consult__body">
              <div className="mol-field">
                <label>{t("consult.nameLabel")}</label>
                <input
                  type="text"
                  value={consult.name}
                  onChange={(e) => setConsult((p) => ({ ...p, name: e.target.value }))}
                  placeholder={t("consult.namePh")}
                  data-testid="consult-name"
                />
                {consultErr.name && <span className="mol-field__err">{consultErr.name}</span>}
              </div>

              <div className="mol-field">
                <label>{t("consult.companyLabel")}</label>
                <input
                  type="text"
                  value={consult.company}
                  onChange={(e) => setConsult((p) => ({ ...p, company: e.target.value }))}
                  placeholder={t("consult.companyPh")}
                  data-testid="consult-company"
                />
                {consultErr.company && <span className="mol-field__err">{consultErr.company}</span>}
              </div>

              <div className="mol-field">
                <label>{t("consult.emailLabel")}</label>
                <input
                  type="email"
                  value={consult.email}
                  onChange={(e) => setConsult((p) => ({ ...p, email: e.target.value }))}
                  placeholder={t("consult.emailPh")}
                  data-testid="consult-email"
                  autoComplete="off"
                />
                {consultErr.email && (
                  <span className="mol-field__err mol-field__err--amber" data-testid="consult-email-err">
                    <span className="mol-tick" /> {consultErr.email}
                  </span>
                )}
              </div>

              <div className="mol-field">
                <label>{t("consult.phoneLabel")}</label>
                <div className="mol-phone">
                  <button
                    type="button"
                    className="mol-phone__cc"
                    onClick={() => setShowCountryDD((v) => !v)}
                    aria-haspopup="listbox"
                    aria-expanded={showCountryDD}
                    data-testid="consult-country"
                  >
                    <span className="mol-phone__flag">{selectedCountry.flag}</span>
                    <span className="mol-phone__code">{selectedCountry.code}</span>
                    <ChevronRightIcon style={{ transform: showCountryDD ? "rotate(90deg)" : "rotate(90deg)" }} />
                  </button>
                  {showCountryDD && (
                    <ul className="mol-phone__dd" role="listbox">
                      {COUNTRIES.map((co) => (
                        <li
                          key={co.c}
                          role="option"
                          aria-selected={consult.country === co.c}
                          onClick={() => {
                            setConsult((p) => ({ ...p, country: co.c }));
                            setShowCountryDD(false);
                          }}
                        >
                          <span className="mol-phone__flag">{co.flag}</span>
                          <span className="mol-phone__name">{co.name}</span>
                          <span className="mol-phone__code">{co.code}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <input
                    type="tel"
                    value={consult.phone}
                    onChange={(e) => setConsult((p) => ({ ...p, phone: e.target.value }))}
                    placeholder={t("consult.phonePh")}
                    className="mol-phone__input"
                    data-testid="consult-phone"
                  />
                </div>
                {consultErr.phone && <span className="mol-field__err">{consultErr.phone}</span>}
              </div>

              <div className="mol-field mol-field--wide">
                <label>{t("consult.briefLabel")}</label>
                <textarea
                  rows={4}
                  value={consult.brief}
                  onChange={(e) => setConsult((p) => ({ ...p, brief: e.target.value }))}
                  placeholder={t("consult.briefPh")}
                  data-testid="consult-brief"
                />
                {consultErr.brief && <span className="mol-field__err">{consultErr.brief}</span>}
              </div>
            </div>
          )}

          {!consultDone && (
            <footer className="mol-consult__footer">
              <button
                type="button"
                className="mol-cta mol-cta--ghost mol-cta--sm"
                onClick={() => setShowConsult(false)}
              >
                <span>{t("consult.cancel")}</span>
              </button>
              <button
                type="submit"
                className="mol-cta mol-cta--primary mol-cta--sm"
                data-testid="consult-submit"
              >
                <span>{t("consult.submit")}</span>
                <span className="mol-cta__glow" aria-hidden="true" />
              </button>
            </footer>
          )}
        </form>
      </div>
    </section>
  );
};

export default OperationsLayer;
